import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { AccessService } from '@app-core/services/access/access.service';
import { DataService } from '@app-core/services/data/data.service';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import {
  COMBINED_SCHEDULED_REPORTS,
  FREQUENCY,
  REPORTS_DURATION_LIST,
  SCHEDULED_REPORTS_LIST_COLUMN,
} from '@app-reports/constants/reports.constants';
import {
  FormAction,
  GetScheduledReportsParams,
  GetScheduledReportsResponse,
  PatchScheduleBody,
  ScheduledReportRow,
} from '@app-reports/models/reports.model';
import { ReportsService } from '@app-reports/services/reports/reports.service';
import { ConfirmDialogComponent } from '@app-shared/components/dialog/confirm-dialog/confirm-dialog.component';
import { EMPTY, Observable, Subject } from 'rxjs';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';

@Component({
  selector: 'app-list-scheduled-reports',
  templateUrl: './list-scheduled-reports.component.html',
  styleUrls: ['./list-scheduled-reports.component.scss'],
})
export class ListScheduledReportsComponent implements OnInit, OnDestroy {
  @ViewChild('paginator', { static: false }) public paginator: MatPaginator;

  public readonly reportTypesList = Object.values(COMBINED_SCHEDULED_REPORTS);
  public readonly frequencyList = Object.values(FREQUENCY);
  public readonly reportDurationList = Object.values(REPORTS_DURATION_LIST);
  public readonly FormAction = FormAction;

  public readonly displayedColumns: string[] = SCHEDULED_REPORTS_LIST_COLUMN;
  public tableSource: MatTableDataSource<any> = new MatTableDataSource([]);
  public pageSize: number = 10;
  private skip = 0;
  public reportsCount: number = 0;
  private searchedReport: string;
  public loader: boolean = false;
  public userId: string;
  public ownerEmail: string = '';
  public searchReport = new FormControl('');
  public actionLoader: boolean = false;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  public userIsAdmin: Boolean = false;

  constructor(
    private dataService: DataService,
    private accessService: AccessService,
    private reportsService: ReportsService,
    private router: Router,
    private snackbarService: SnackBarService,
    private dialog: MatDialog,
    public translate: TranslateService,
    private mixpanelService: MixPanelService
  ) {}

  ngOnInit(): void {
    this.userIsAdmin = this.accessService.isAdminRole();
    this.userId = this.accessService.getLoginInfo().userId || this.accessService.getLoginInfo()?.userHash;
    this.ownerEmail = this.accessService.getLoginInfo().loginName;

    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      this.userIsAdmin = this.accessService.isAdminRole();
      if (this.searchReport.value) {
        this.searchReport.setValue('');
        this.searchedReport = '';
      }
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
      this.skip = 0;
      this.getScheduledReportsList(true);
    });

    this.translate.onLangChange.pipe(takeUntil(this.ngUnsubscribe)).subscribe(() => {
      if (this.tableSource?.data?.length > 0 && this.tableSource.data[0]) {
        this.addReportLabels(this.tableSource.data);
      }
    });
  }

  onPageChange(event: PageEvent) {
    this.skip = event.pageIndex * this.pageSize;
    this.getScheduledReportsList();
  }

  onRefresh() {
    this.mixpanelService.track('Refresh Scheduled report Clicked');
    this.getScheduledReportsList(true);
  }

  public getScheduledReportsList(isRefresh = false) {
    this.loader = true;
    this.tableSource.data = new Array(this.pageSize).fill(undefined);

    const searchString = this.searchedReport || '';
    const params: GetScheduledReportsParams = {
      limit: this.pageSize,
      offset: this.skip,
      ...(searchString?.trim().length ? { scheduleName: searchString.trim() } : {}),
    };

    this.reportsService
      .getScheduledReports(params, isRefresh)
      .pipe(
        finalize(() => {
          this.loader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        (res: GetScheduledReportsResponse) => {
          let data = res?.rows || [];
          this.addReportLabels(data);
          this.tableSource.data = data;
          this.reportsCount = res?.totalCount || 0;
        },
        () => {
          this.tableSource.data = [];
          this.reportsCount = 0;
        }
      );
  }

  private addReportLabels(rows: ScheduledReportRow[]) {
    rows.forEach((row) => {
      row['labels'] = {};
      if (row.reportType === 'COACHING_SESSION') {
        row.scheduleConfiguration.frequency = 'IMMEDIATE';
      }
      row['labels']['reportTypeLabel'] = this.reportTypeLabel(row) || '';
      row['labels']['frequencyLabel'] = this.frequencyList.find((fr) => fr.key === row.scheduleConfiguration.frequency)?.label || '';
      row['labels']['reportDurationLabel'] =
        this.reportDurationList.find((duration) => duration.key === row.scheduleConfiguration.reportDurationRange)?.label || '';
    });
  }

  private reportTypeLabel(row: ScheduledReportRow) {
    const reportType = this.reportTypesList.find((type) => type.key === row.reportType);
    const reportTypeLabel = reportType?.label ? this.translate.instant(reportType.label) : '';

    const reportSubtypeKey = this.reportsService.getSubtypeKey(row?.reportType, row?.scheduleConfiguration) || 'ALL';
    const reportSubtype = reportType?.subtypes?.find((subtype) => subtype.key === reportSubtypeKey);
    const reportSubtypeLabel = reportSubtype?.label ? this.translate.instant(reportSubtype.label) : '';

    return reportSubtypeLabel ? `${reportTypeLabel} (${reportSubtypeLabel})` : reportTypeLabel;
  }

  public editSchedule(row: ScheduledReportRow) {
    this.mixpanelService.track('Edit Scheduled Report Clicked', {
      'Report Type': row.reportType,
      Frequency: row?.scheduleConfiguration?.frequency || '',
      'Scheduled Id': row.scheduleId,
    });
    this.navigateToCreateSchedule(FormAction.Edit, row);
  }

  public duplicateSchedule(row: ScheduledReportRow) {
    this.mixpanelService.track('Duplicate Schedule Clicked');
    this.navigateToCreateSchedule(FormAction.Duplicate, row);
  }

  private navigateToCreateSchedule(action: FormAction = FormAction.Create, row?: ScheduledReportRow) {
    row && (this.reportsService.scheduleDetails = row);
    this.router.navigate(['/reports/create-schedule'], {
      queryParams: {
        ...(row?.scheduleId ? { scheduleId: row.scheduleId } : {}),
        action,
      },
    });
    if (action === FormAction.Create) {
      this.mixpanelService.track('Create Schedule Clicked');
    }
  }

  public pauseSchedule(row: ScheduledReportRow) {
    const scheduleId = row?.scheduleId || '';
    const reportType = row?.reportType || '';
    const scheduleName = row?.scheduleName || '';
    const status = row?.active;

    if (!scheduleId || !reportType || status === undefined) {
      return;
    }

    this.actionLoader = true;

    const body: PatchScheduleBody = {
      reportType,
      active: !status,
    };

    this.reportsService
      .updateScheduledReport(scheduleId, body)
      .pipe(
        finalize(() => {
          this.actionLoader = false;
        }),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(
        () => {
          this.mixpanelService.track('Pause Scheduled Clicked', {
            'Report Name': scheduleName,
            'User Id': this.userId,
            'Scheduled Id': scheduleId,
          });
          this.snackbarService.success(
            this.translate.instant('scheduleStatusMessage', {
              scheduleName: scheduleName,
              status: status ? this.translate.instant('paused') : this.translate.instant('activated'),
            })
          );

          this.getScheduledReportsList();
        },
        () => {
          this.snackbarService.success(
            this.translate.instant('scheduleFailedMessage', {
              scheduleName: scheduleName,
              status: status ? this.translate.instant('pause') : this.translate.instant('activate'),
            })
          );
        }
      );
  }

  public deleteSchedule(row: ScheduledReportRow) {
    const scheduleId = row?.scheduleId || '';
    const scheduleName = row?.scheduleName || '';

    if (!scheduleId) {
      return;
    }
    this.mixpanelService.track('Delete Schedule Clicked', {
      'Report Name': scheduleName,
      'User Id': this.userId,
      'Scheduled Id': scheduleId,
    });

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      position: { top: '24px' },
      width: '400px',
      data: {
        title: this.translate.instant('Confirmation Required'),
        content: `${this.translate.instant('This action cannot be undone. ')}  ${scheduleName} ${this.translate.instant(
          ' will be deleted permanently.'
        )}`,
        actions: [
          { actionButton: this.translate.instant('CANCEL'), dialogCloseValue: false },
          { actionButton: this.translate.instant('DELETE'), dialogCloseValue: true, primary: true },
        ],
      },
    });

    dialogRef
      .afterClosed()
      .pipe(
        takeUntil(this.ngUnsubscribe),
        switchMap((confirmDelete: boolean = false) => {
          return confirmDelete ? this.deleteScheduledReport(scheduleId) : EMPTY;
        })
      )
      .subscribe(
        () => {
          this.mixpanelService.track('Delete Schedule Report - Confimed', {
            'Report Name': scheduleName,
            'User Id': this.userId,
            'Scheduled Id': scheduleId,
          });
          this.snackbarService.success(this.translate.instant('scheduleDeletedMessage', { scheduleName: scheduleName }));
          this.getScheduledReportsList();
        },
        () => {
          this.snackbarService.failure(this.translate.instant('scheduleDeleteFailed', { scheduleName: scheduleName }));
        }
      );
  }

  private deleteScheduledReport(scheduleId: string): Observable<any> {
    this.actionLoader = true;
    return this.reportsService.deleteScheduledReport(scheduleId).pipe(
      finalize(() => {
        this.actionLoader = false;
      }),
      takeUntil(this.ngUnsubscribe)
    );
  }

  public openFAQ() {
    this.dataService?.openFaq('scheduledReports');
  }

  public onSearchReport() {
    this.searchedReport = this.searchReport?.value?.trim() || '';
    if (this.paginator?.pageIndex === 0) {
      this.skip = 0;
      this.getScheduledReportsList();
    } else {
      this.paginator?.firstPage();
    }

    this.mixpanelService.track('Searching Scheduled report', {
      'Searched Schedled Report': this.searchedReport,
      'User Id': this.userId,
    });
  }

  public get showScheduledReportsList(): boolean {
    const report = this.searchedReport || '';
    return !!(this.tableSource.data.length || report);
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public clearSearchedReport() {
    this.searchReport.reset();
    if (this.searchedReport) {
      this.searchedReport = '';
      this.onSearchReport();
    }
  }
}
